/**
 * API functions for Code Browser feature
 */

import type { FileTreeResponse, FileContent, CodeApiError } from '@/types/code'
import type { Extension } from '@codemirror/state'
import { javascript } from '@codemirror/lang-javascript'
import { python } from '@codemirror/lang-python'
import { html } from '@codemirror/lang-html'
import { css } from '@codemirror/lang-css'
import { json } from '@codemirror/lang-json'
import { markdown } from '@codemirror/lang-markdown'
import { xml } from '@codemirror/lang-xml'
import { sql } from '@codemirror/lang-sql'

/**
 * Image file extensions that can be previewed inline
 */
const IMAGE_EXTENSIONS = new Set(['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.ico', '.bmp'])

/**
 * Binary file extensions (includes images and other non-text files)
 */
const BINARY_EXTENSIONS = new Set([
  ...IMAGE_EXTENSIONS,
  // Fonts
  '.woff', '.woff2', '.ttf', '.eot', '.otf',
  // Documents
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  // Archives
  '.zip', '.tar', '.gz', '.rar', '.7z',
  // Executables
  '.exe', '.dll', '.so', '.dylib',
  // Media
  '.mp3', '.mp4', '.wav', '.avi', '.mov', '.mkv',
  // Databases
  '.sqlite', '.db',
])

/**
 * Check if a file path is an image file
 */
export function isImageFile(path: string): boolean {
  const ext = '.' + (path.split('.').pop()?.toLowerCase() || '')
  return IMAGE_EXTENSIONS.has(ext)
}

/**
 * Check if a file path is a binary file
 */
export function isBinaryFile(path: string): boolean {
  const ext = '.' + (path.split('.').pop()?.toLowerCase() || '')
  return BINARY_EXTENSIONS.has(ext)
}

/**
 * Get download URL for a file
 */
export function getDownloadUrl(projectId: number, path: string): string {
  return `/api/projects/${projectId}/code/download?path=${encodeURIComponent(path)}`
}

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

async function handleApiResponse<T>(response: Response, fallbackError: string): Promise<T> {
  if (!response.ok) {
    let errorMessage = fallbackError
    try {
      const data = await response.json() as CodeApiError
      errorMessage = data.error || errorMessage
    } catch {
      // Response wasn't JSON (e.g., 502 HTML page)
    }
    throw new Error(errorMessage)
  }
  return response.json() as Promise<T>
}

/**
 * Fetch the file tree for a project
 */
export async function fetchFileTree(projectId: number): Promise<FileTreeResponse> {
  const response = await fetch(`/api/projects/${projectId}/code/tree`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
  })
  return handleApiResponse<FileTreeResponse>(response, 'Failed to fetch file tree')
}

/**
 * Fetch the content of a specific file
 */
export async function fetchFileContent(projectId: number, path: string): Promise<FileContent> {
  const response = await fetch(`/api/projects/${projectId}/code/file?path=${encodeURIComponent(path)}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
  })
  return handleApiResponse<FileContent>(response, 'Failed to fetch file content')
}

/**
 * Get CodeMirror language extension for a file path
 * Returns null for unsupported languages (falls back to plain text)
 */
export function getLanguageExtension(filePath: string): Extension | null {
  const ext = filePath.split('.').pop()?.toLowerCase() || ''
  const name = filePath.split('/').pop() || ''

  // JavaScript/TypeScript
  if (['js', 'jsx', 'mjs', 'cjs'].includes(ext)) return javascript({ jsx: true })
  if (['ts', 'tsx'].includes(ext)) return javascript({ jsx: true, typescript: true })

  // Ruby - No official CodeMirror 6 support, falls back to plain text
  if (['rb', 'erb', 'rake', 'gemspec'].includes(ext) || ['Gemfile', 'Rakefile'].includes(name)) {
    return null
  }

  // Python
  if (['py', 'pyw', 'pyx'].includes(ext)) return python()

  // Web
  if (['html', 'htm'].includes(ext)) return html()
  if (['css', 'scss', 'sass', 'less'].includes(ext)) return css()

  // Data formats
  if (ext === 'json') return json()
  if (['md', 'mdx'].includes(ext)) return markdown()
  if (['xml', 'svg'].includes(ext)) return xml()
  if (ext === 'sql') return sql()

  return null // Plain text fallback
}
